<template>
  <div class="slide-canvas-wrapper">
    <canvas
      ref="canvasRef"
      class="slide-canvas"
      :style="canvasStyle"
      tabindex="0"
      @pointerdown="handlePointerDown"
      @pointermove="handleCanvasPointerMove"
      @pointerup="handleCanvasPointerUp"
      @pointercancel="handleCanvasPointerCancel"
      @keydown="handleKeydown"
      @contextmenu="handleContextMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import type { PropType } from 'vue';
import {
  SlideCanvasBox,
  SlideCanvasPoint,
  SlideRegionDict,
  SlideRenderBox,
  SlideScalingContext,
} from './SlideCanvas.types';
import { useSlideRegions } from '../composables/useSlideRegions';
import { useSlideCanvasRenderer } from '../composables/useSlideCanvasRenderer';
import { useSlideInteraction } from '../composables/useSlideInteraction';
import { resolveLabelPalette } from '../utils/slideLabelColors';
import {
  getCanvasPointRaw as getCanvasPointRawUtil,
  toNaturalPoint,
} from '../utils/slideHitTesting';

const SELECTED_STROKE = '#3fb950';
const SELECTED_FILL = 'rgba(63, 185, 80, 0.18)';

const props = defineProps({
  width: { type: Number, default: 512 },
  height: { type: Number, default: 512 },
  regions: { type: Object as PropType<SlideRegionDict>, default: null },
  activeLabel: { type: String, default: null },
  fallbackLabel: { type: String, default: 'default' },
  stroke: { type: String, default: '#ff4d4f' },
  fill: { type: String, default: 'rgba(255, 77, 79, 0.12)' },
  draftStroke: { type: String, default: '#2f54eb' },
  draftFill: { type: String, default: 'rgba(47, 84, 235, 0.12)' },
  interactive: { type: Boolean, default: true },
  background: { type: String, default: '#ffffff' },
  backgroundImage: { type: String, default: null },
  autoSync: { type: Boolean, default: false },
  labelFilter: { type: Object as PropType<Record<string, boolean> | null>, default: null },
});

const emit = defineEmits<{
  (e: 'update:regions', regions: SlideRegionDict): void;
  (e: 'draw-start', point: SlideCanvasPoint): void;
  (e: 'draw-end', box: SlideCanvasBox): void;
  (e: 'box-contextmenu', payload: { box: SlideRenderBox; clientX: number; clientY: number }): void;
}>();

const backgroundImageRef = ref<HTMLImageElement | null>(null);
let backgroundImageToken = 0;

const scaleX = computed(() => {
  if (backgroundImageRef.value && backgroundImageRef.value.naturalWidth > 0) {
    return props.width / backgroundImageRef.value.naturalWidth;
  }
  return 1;
});

const scaleY = computed(() => {
  if (backgroundImageRef.value && backgroundImageRef.value.naturalHeight > 0) {
    return props.height / backgroundImageRef.value.naturalHeight;
  }
  return 1;
});

const naturalMaxWidth = computed(() => props.width / scaleX.value);
const naturalMaxHeight = computed(() => props.height / scaleY.value);

const scalingContext = computed<SlideScalingContext>(() => ({
  width: props.width,
  height: props.height,
  scaleX: scaleX.value,
  scaleY: scaleY.value,
  naturalMaxWidth: naturalMaxWidth.value,
  naturalMaxHeight: naturalMaxHeight.value,
}));

const defaultLabel = computed(() => props.activeLabel || props.fallbackLabel || 'default');

const slideRegions = useSlideRegions(() => defaultLabel.value);
const selectedBoxId = computed(() => slideRegions.selected.value?.id ?? null);
const selectedRenderBox = slideRegions.selectedRenderBox;

function emitRegionChange() {
  if (!props.autoSync) return;
  emit('update:regions', slideRegions.serializeRegions());
}

const {
  draftBox,
  handlePointerDown,
  handlePointerMove: handleCanvasPointerMove,
  handlePointerUp: handleCanvasPointerUp,
  handlePointerCancel: handleCanvasPointerCancel,
  stopInteraction,
  canvasCursor,
} = useSlideInteraction({
  scalingContext,
  interactive: toRef(props, 'interactive'),
  selectedRenderBox,
  slideRegions,
  onDrawStart: (p) => emit('draw-start', p),
  onDrawEnd: (b) => emit('draw-end', b),
  onRegionChange: emitRegionChange,
  scheduleDraw: () => scheduleDraw(),
  getDefaultLabel: () => defaultLabel.value,
});

const resolveBoxColor = (label: string) => resolveLabelPalette(label, props.stroke, props.fill);

const { canvasRef, scheduleDraw, syncContext } = useSlideCanvasRenderer({
  width: toRef(props, 'width'),
  height: toRef(props, 'height'),
  background: toRef(props, 'background'),
  backgroundImage: backgroundImageRef,
  stroke: toRef(props, 'stroke'),
  fill: toRef(props, 'fill'),
  draftStroke: toRef(props, 'draftStroke'),
  draftFill: toRef(props, 'draftFill'),
  selectedStroke: SELECTED_STROKE,
  selectedFill: SELECTED_FILL,
  renderBoxes: slideRegions.renderBoxes,
  draftBox,
  selectedBoxId,
  resolveColor: resolveBoxColor,
});

const canvasStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
  touchAction: 'none',
  cursor: canvasCursor.value,
  border: '1px solid rgba(255, 255, 255, 0.2)',
  display: 'block',
}));

function getCanvasPointRaw(event: PointerEvent | MouseEvent): SlideCanvasPoint | null {
  const canvas = canvasRef.value;
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  return getCanvasPointRawUtil(
    event.clientX,
    event.clientY,
    { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    props.width,
    props.height,
  );
}

function getCanvasPoint(event: PointerEvent | MouseEvent): SlideCanvasPoint | null {
  const raw = getCanvasPointRaw(event);
  if (!raw) return null;
  return toNaturalPoint(raw, scaleX.value, scaleY.value);
}

function handleContextMenu(event: MouseEvent) {
  if (!props.interactive) return;
  const point = getCanvasPoint(event);
  if (!point) return;
  const hit = slideRegions.selectAtPoint(point);
  if (!hit) return;
  event.preventDefault();
  canvasRef.value?.focus();
  scheduleDraw();
  emit('box-contextmenu', {
    box: hit,
    clientX: event.clientX,
    clientY: event.clientY,
  });
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.interactive) return;
  if (event.key !== 'Delete' && event.key !== 'Backspace') return;
  const removed = slideRegions.removeSelected();
  if (!removed) return;
  event.preventDefault();
  emitRegionChange();
  scheduleDraw();
}

function updateBoxLabel(boxId: string, nextLabel: string): boolean {
  const updated = slideRegions.updateBoxLabel(boxId, nextLabel);
  if (!updated) return false;
  emitRegionChange();
  scheduleDraw();
  return true;
}

watch(
  () => props.regions,
  (regions) => {
    const payload = regions && Object.keys(regions).length > 0 ? regions : undefined;
    slideRegions.loadRegionsDict(payload);
    scheduleDraw();
  },
  { deep: true, immediate: true },
);

watch(
  () => props.labelFilter,
  (filter) => {
    slideRegions.setLabelFilter(filter ?? null);
    scheduleDraw();
  },
  { deep: true, immediate: true },
);

watch(
  () => props.backgroundImage,
  (src) => {
    loadBackgroundImage(src);
  },
  { immediate: true },
);

function loadBackgroundImage(source: string | null) {
  backgroundImageToken += 1;
  const currentToken = backgroundImageToken;
  backgroundImageRef.value = null;
  if (!source) {
    scheduleDraw();
    return;
  }
  const image = new Image();
  image.onload = () => {
    if (currentToken !== backgroundImageToken) return;
    backgroundImageRef.value = image;
    scheduleDraw();
  };
  image.onerror = () => {
    if (currentToken !== backgroundImageToken) return;
    backgroundImageRef.value = null;
    scheduleDraw();
  };
  image.src = source;
}

onMounted(syncContext);

onBeforeUnmount(stopInteraction);

defineExpose({
  serializeRegions: slideRegions.serializeRegions,
  serializeBoxes: slideRegions.serializeBoxes,
  updateBoxLabel,
});
</script>

<style scoped>
.slide-canvas-wrapper {
  display: inline-block;
}

.slide-canvas {
  background-color: transparent;
}
</style>
